import { Link } from "react-router-dom";
import { ArrowRight, Repeat, CalendarClock, Sun } from "lucide-react";
import ServicePage from "../components/site/ServicePage";
import FadeUp from "../components/site/FadeUp";
import { getService } from "../data/site";

const TASKS = [
  "Laundry — washed, folded, and put away",
  "Kitchen reset",
  "Bathroom refresh",
  "Vacuuming",
  "Tidying throughout",
  "Pantry & closet organization",
  "Bed making",
  "Refrigerator clean out",
  "Package returns",
  "Grocery shopping",
  "Restocking essentials",
  "Donation drop-offs",
  "Small home projects",
  "Seasonal organization",
  "Custom tasks — just ask",
];

const SUPPORT_OPTIONS = [
  {
    icon: Repeat,
    title: "Ongoing Home Management",
    body: "Consistent weekly support for families who want to take home management off their plate.",
    tags: ["3-month commitment", "Priority scheduling", "Best value"],
    featured: true,
  },
  {
    icon: CalendarClock,
    title: "Short-Term Home Support",
    body: "Need an extra set of hands for a busy month or season? Harbor Home offers 1–2 month support based on availability.",
    tags: ["1–2 months", "Based on availability"],
  },
  {
    icon: Sun,
    title: "Seasonal Concierge",
    body: "Back-to-school resets, holiday preparation, winter property support, spring organization and summer home services — extra help when life gets especially busy.",
    tags: ["Back-to-school", "Holidays", "Spring & summer"],
  },
];

const SupportOptions = () => (
  <section data-testid="support-options" className="section-pad">
    <div className="container-hh">
      <FadeUp className="max-w-2xl">
        <p className="eyebrow">Choose the support that fits your season</p>
        <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">
          Support that flexes with your life
        </h2>
      </FadeUp>

      <div className="mt-10 grid gap-8 md:grid-cols-3">
        {SUPPORT_OPTIONS.map((o, i) => (
          <FadeUp key={o.title} delay={i * 0.1}>
            <div
              data-testid={`support-card-${i}`}
              className={`flex h-full flex-col rounded-[12px] p-8 shadow-cozy transition-all duration-300 hover:shadow-cozy-lg ${
                o.featured ? "bg-navy text-white" : "bg-white"
              }`}
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${
                  o.featured ? "bg-white/10" : "bg-seafoam"
                }`}
              >
                <o.icon className="h-6 w-6 text-teal" />
              </div>
              <h3 className={`mt-5 text-2xl ${o.featured ? "text-white" : ""}`}>
                {o.title}
              </h3>
              <p
                className={`mt-3 flex-1 leading-relaxed ${
                  o.featured ? "text-white/80" : "text-bodytext"
                }`}
              >
                {o.body}
              </p>
              <div className="mt-5 flex flex-wrap gap-2 pt-1">
                {o.tags.map((t) => (
                  <span
                    key={t}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      o.featured
                        ? "bg-teal text-white"
                        : "bg-seafoam text-navy"
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp className="mt-10 text-center">
        <Link to="/book" data-testid="find-the-right-fit" className="btn-teal">
          Find the Right Fit <ArrowRight className="h-4 w-4" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function WeeklyConcierge() {
  return (
    <ServicePage
      service={getService("weekly-home-concierge")}
      tagline="Your home, quietly handled every week."
      intro="Weekly Home Management is a standing relationship, not a one-off clean. The same trusted team learns your home and returns each week to keep everything running — so you get your time, and your headspace, back."
      tasksTitle="Your weekly task list"
      tasks={TASKS}
      afterContent={<SupportOptions />}
    />
  );
}
